<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { REPLY_EDITOR_MODES } from '../constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
  // Zimobi: toggles the personal WhatsApp link chip
  showWhatsapp: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['toggleMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);

/**
 * Computed boolean indicating if the editor is in private note mode
 * When isReplyRestricted is true, force switch to private note
 * Otherwise, respect the current mode prop
 * @type {ComputedRef<boolean>}
 */
const isPrivate = computed(() => {
  if (props.isReplyRestricted) {
    // Force switch to private note when replies are restricted
    return true;
  }
  // Otherwise respect the current mode
  return props.mode === REPLY_EDITOR_MODES.NOTE;
});

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  const widthToUse = isPrivate.value
    ? privateModeSize.width.value
    : replyModeSize.width.value;

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the width of reply mode + padding when in private mode
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  const xTranslate = isPrivate.value ? replyModeSize.width.value + 16 : 0;

  return `${xTranslate}px`;
});

// Zimobi: current contact + personal WhatsApp link computed inside the component
// (mirrors currentContactMixin + PersonalWhatsappButton) instead of props.
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const currentContact = computed(() =>
  store.getters['contacts/getContact'](currentChat.value?.meta?.sender?.id)
);
const contactFirstName = computed(
  () => currentContact.value?.name?.trim().split(' ')[0] ?? ''
);
const contactPhoneNumber = computed(() =>
  String(currentContact.value?.phone_number ?? '').trim()
);
const link = computed(
  () =>
    `https://api.whatsapp.com/send?phone=${contactPhoneNumber.value}&text=Ol%C3%A1%20${contactFirstName.value},%20tudo%20bem?`
);

// Zimobi: only render the chip when the link is fully valid, i.e. the contact
// has both a non-blank first name and a non-blank phone number.
const isWhatsappLinkVisible = computed(
  () =>
    props.showWhatsapp &&
    Boolean(contactFirstName.value) &&
    Boolean(contactPhoneNumber.value)
);
</script>

<template>
  <!--
    Zimobi copy of EditorModeToggle: the Reply/Private Note toggle keeps its own
    <button>, while the optional WhatsApp chip is a sibling link inside the same
    pill so it opens a link instead of toggling the editor mode.
  -->
  <div
    class="flex items-center w-auto h-8 transition-all border rounded-full bg-n-alpha-2 group duration-300 ease-in-out z-0"
  >
    <button
      class="flex items-center p-1 relative active:scale-[0.995] active:duration-75"
      :disabled="disabled || isReplyRestricted"
      :class="{
        'cursor-not-allowed': disabled || isReplyRestricted,
      }"
      @click="$emit('toggleMode')"
    >
      <div ref="wootEditorReplyMode" class="flex items-center gap-1 px-2 z-20">
        {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
      </div>
      <div
        ref="wootEditorPrivateMode"
        class="flex items-center gap-1 px-2 z-20"
      >
        {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
      </div>
      <div
        class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
        :class="{
          'transition-all duration-300': !disabled && !isReplyRestricted,
        }"
        :style="{
          '--chip-width': width,
          '--translate-x': translateValue,
          '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
        }"
      />
    </button>
    <!-- Zimobi: agent's personal WhatsApp button as a link-only chip -->
    <a
      v-if="isWhatsappLinkVisible"
      :href="link"
      target="_blank"
      rel="noopener nofollow noreferrer"
      class="flex items-center h-full pl-3 pr-4 text-sm whitespace-nowrap text-n-slate-11 hover:text-n-teal-11 z-20"
    >
      <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
      Abrir Whatsapp
      <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
    </a>
  </div>
</template>
